import { useLocation, useNavigate } from 'react-router-dom';
import { useEffect } from 'react';

import SpinnerFullPage from './SpinnerFullPage';
import { useSession } from '../features/authentication/useSession';

// WHY: Define interface for ProtectedRoute props
// Route wrapper that enforces authentication
// Redirects unauthenticated users to signin with redirect param
// Shows loading spinner during authentication check
export interface ProtectedRouteProps {
  children: React.ReactNode; // Protected content to render when authenticated
}

function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { pathname, search } = useLocation();
  const navigate = useNavigate();

  // WHY: Load the authenticated user session
  const { isValidSession, isLoading, error } = useSession();

  // WHY: General authentication check and redirect
  // Redirects to signin with current path as redirect parameter
  useEffect(() => {
    if (!isLoading && (error || !isValidSession)) {
      // WHY encodeURIComponent instead of encodeURI:
      // encodeURI does NOT encode ?, &, = — so query params from the original
      // URL bleed into /signin as separate params, mangling the redirect value.
      // encodeURIComponent encodes the full path+search as a single param value,
      // preserving all original query params (code, new_email, etc.) intact.
      navigate(`/signin?redirect=${encodeURIComponent(pathname + search)}`);
    }
  }, [isLoading, error, isValidSession, navigate, pathname, search]);

  // WHY: Show spinner while session is being validated
  if (isLoading) return <SpinnerFullPage />;

  // WHY: Render protected content when session is valid
  return children;
}

export default ProtectedRoute;
